import { useId } from 'react';
import type { RefObject } from 'react';
import type { JSONContent } from '@tiptap/core';
import type {
  ComparisonSliderContentType,
  ComparisonSliderOption,
} from '@course-authoring/shared-types';
import { useImageSourceWithFallback } from '@course-authoring/shared-types';

import { ComparisonSliderFieldEditor } from './ComparisonSliderFieldEditor';
import type {
  ComparisonSliderFieldName,
  ComparisonSliderOptionKey,
} from './comparisonSliderEditorTypes';
import type { ComparisonSliderEditorStyles } from './comparisonSliderEditorStyles';

const VIDEO_SHORT_DESCRIPTION_MAX_LENGTH = 175;

const CAMERA_ICON_SVG = (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ opacity: 0.6 }}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="12" cy="13" r="4" />
    <line x1="2" y1="2" x2="8" y2="2" />
  </svg>
);

const VIDEO_ICON_SVG = (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ opacity: 0.6 }}
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <polygon points="10,8 16,12 10,16" />
  </svg>
);

interface ComparisonSliderOptionSectionProps {
  optionKey: ComparisonSliderOptionKey;
  optionLabel: string;
  otherOptionLabel: string;
  option: ComparisonSliderOption;
  contentType: ComparisonSliderContentType;
  focusedField: ComparisonSliderFieldName | null;
  onFieldFocus: (fieldName: ComparisonSliderFieldName) => void;
  onFieldBlur: () => void;
  onFieldChange: (
    fieldName: ComparisonSliderFieldName,
    content: JSONContent
  ) => void;
  onCollapsedLabelChange: (option: ComparisonSliderOptionKey, value: string) => void;
  onImageAltChange: (option: ComparisonSliderOptionKey, imageAlt: string) => void;
  onOpenImageModal: (option: ComparisonSliderOptionKey) => void;
  onRemoveImage: (option: ComparisonSliderOptionKey) => void;
  onOpenVideoModal: (option: ComparisonSliderOptionKey) => void;
  onRemoveVideo: (option: ComparisonSliderOptionKey) => void;
  onVideoMetadataChange: (
    option: ComparisonSliderOptionKey,
    updates: { videoTitle?: string; videoShortDescription?: string }
  ) => void;
  containerRef: RefObject<HTMLDivElement | null>;
  styles: ComparisonSliderEditorStyles;
  translationMode?: boolean;
}

export function ComparisonSliderOptionSection({
  optionKey,
  optionLabel,
  otherOptionLabel,
  option,
  contentType,
  focusedField,
  onFieldFocus,
  onFieldBlur,
  onFieldChange,
  onCollapsedLabelChange,
  onImageAltChange,
  onOpenImageModal,
  onRemoveImage,
  onOpenVideoModal,
  onRemoveVideo,
  onVideoMetadataChange,
  containerRef,
  styles,
  translationMode = false,
}: ComparisonSliderOptionSectionProps) {
  const titleField = `${optionKey}.title` as ComparisonSliderFieldName;
  const bodyField = `${optionKey}.body` as ComparisonSliderFieldName;
  const collapsedLabelId = useId();
  const videoTitleId = useId();
  const videoDescId = useId();

  const {
    resolvedSrc: resolvedPosterUrl,
    handleError: handlePosterError,
  } = useImageSourceWithFallback(option.videoPosterUrl);

  const currentShortDescLength = (option.videoShortDescription || '').length;

  return (
    <div style={styles.optionPanel}>
      <div style={styles.optionHeader}>{optionLabel}</div>

      {/* Collapsed label area */}
      <div style={styles.collapsedLabelArea}>
        <div style={styles.panelFieldLabel}>
          Collapsed label <span style={{ fontWeight: 400 }}>optional</span>
        </div>
        <input
          id={collapsedLabelId}
          type="text"
          value={option.collapsedLabel || ''}
          onChange={(e) => onCollapsedLabelChange(optionKey, e.target.value)}
          placeholder={`See ${otherOptionLabel}`}
          style={styles.collapsedLabelInput}
          aria-label={`${optionLabel} collapsed label`}
        />
        <div style={styles.collapsedLabelHelper}>
          Shown on {otherOptionLabel} when {optionLabel} is expanded — this is
          what the learner clicks to switch sides.
        </div>
      </div>

      {/* Content fields based on contentType */}
      {contentType === 'video' ? (
        <VideoFields
          optionKey={optionKey}
          optionLabel={optionLabel}
          option={option}
          resolvedPosterUrl={resolvedPosterUrl}
          handlePosterError={handlePosterError}
          currentShortDescLength={currentShortDescLength}
          videoTitleId={videoTitleId}
          videoDescId={videoDescId}
          onOpenVideoModal={onOpenVideoModal}
          onRemoveVideo={onRemoveVideo}
          onVideoMetadataChange={onVideoMetadataChange}
          styles={styles}
          translationMode={translationMode}
        />
      ) : (
        <TextFields
          optionKey={optionKey}
          optionLabel={optionLabel}
          option={option}
          contentType={contentType}
          titleField={titleField}
          bodyField={bodyField}
          focusedField={focusedField}
          onFieldFocus={onFieldFocus}
          onFieldBlur={onFieldBlur}
          onFieldChange={onFieldChange}
          onImageAltChange={onImageAltChange}
          onOpenImageModal={onOpenImageModal}
          onRemoveImage={onRemoveImage}
          containerRef={containerRef}
          styles={styles}
          translationMode={translationMode}
        />
      )}
    </div>
  );
}

/* ── Text + Image / Text Only fields ────────────────────────────────── */

interface TextFieldsProps {
  optionKey: ComparisonSliderOptionKey;
  optionLabel: string;
  option: ComparisonSliderOption;
  contentType: ComparisonSliderContentType;
  titleField: ComparisonSliderFieldName;
  bodyField: ComparisonSliderFieldName;
  focusedField: ComparisonSliderFieldName | null;
  onFieldFocus: (fieldName: ComparisonSliderFieldName) => void;
  onFieldBlur: () => void;
  onFieldChange: (
    fieldName: ComparisonSliderFieldName,
    content: JSONContent
  ) => void;
  onImageAltChange: (option: ComparisonSliderOptionKey, imageAlt: string) => void;
  onOpenImageModal: (option: ComparisonSliderOptionKey) => void;
  onRemoveImage: (option: ComparisonSliderOptionKey) => void;
  containerRef: RefObject<HTMLDivElement | null>;
  styles: ComparisonSliderEditorStyles;
  translationMode: boolean;
}

function TextFields({
  optionKey,
  optionLabel,
  option,
  contentType,
  titleField,
  bodyField,
  focusedField,
  onFieldFocus,
  onFieldBlur,
  onFieldChange,
  onImageAltChange,
  onOpenImageModal,
  onRemoveImage,
  containerRef,
  styles,
  translationMode,
}: TextFieldsProps) {
  return (
    <>
      <div>
        <div style={styles.panelFieldLabel}>
          Panel heading <span style={{ fontWeight: 400 }}>optional</span>
        </div>
        <ComparisonSliderFieldEditor
          content={option.title}
          placeholder="Option 1"
          isFocused={focusedField === titleField}
          onFocus={() => onFieldFocus(titleField)}
          onBlur={onFieldBlur}
          onContentChange={(content) => onFieldChange(titleField, content)}
          autoFocus={false}
          containerRef={containerRef}
          editorStyle={styles.optionTitleEditor}
          defaultBold
        />
      </div>

      <div>
        <div style={styles.panelFieldLabel}>Body text</div>
        <ComparisonSliderFieldEditor
          content={option.body}
          placeholder="Body text..."
          allowLists={true}
          isFocused={focusedField === bodyField}
          onFocus={() => onFieldFocus(bodyField)}
          onBlur={onFieldBlur}
          onContentChange={(content) => onFieldChange(bodyField, content)}
          autoFocus={false}
          containerRef={containerRef}
          editorStyle={styles.optionBodyEditor}
        />
      </div>

      {contentType === 'text-image' && (
        <>
          {translationMode ? (
            option.imageUrl ? (
              <img src={option.imageUrl} alt={option.imageAlt || ''} style={styles.imagePreview} />
            ) : null
          ) : <div>
            <div style={styles.panelFieldLabel}>Image</div>
            <button
              type="button"
              style={{
                ...styles.imagePickerButton,
                ...(option.imageUrl ? { border: 'none' } : {}),
              }}
              onClick={() => onOpenImageModal(optionKey)}
              aria-label={
                option.imageUrl
                  ? `Change ${optionLabel.toLowerCase()} image`
                  : `Add ${optionLabel.toLowerCase()} image`
              }
            >
              {option.imageUrl ? (
                <>
                  <img
                    src={option.imageUrl}
                    alt={option.imageAlt || ''}
                    style={styles.imagePreview}
                  />
                  <div style={styles.imagePickerOverlay}>Click to change image</div>
                </>
              ) : (
                <div style={styles.imagePlaceholder}>
                  {CAMERA_ICON_SVG}
                  <span>Click to add image</span>
                </div>
              )}
            </button>
          </div>}

          {!translationMode && option.imageUrl && (
            <button
              type="button"
              style={styles.removeImageButton}
              onClick={() => onRemoveImage(optionKey)}
              aria-label={`Remove ${optionLabel.toLowerCase()} image`}
            >
              Remove image
            </button>
          )}

          {!translationMode ? <><div style={styles.fieldLabel}>Alt Text</div>
          <input
            type="text"
            value={option.imageAlt || ''}
            onChange={(event) => onImageAltChange(optionKey, event.target.value)}
            placeholder="Describe the image (min 5 characters)"
            style={styles.imageInput}
            aria-label={`${optionLabel} image alt text`}
            disabled={!option.imageUrl}
          /></> : null}
        </>
      )}
    </>
  );
}

/* ── Video fields ──────────────────────────────────────────────────── */

interface VideoFieldsProps {
  optionKey: ComparisonSliderOptionKey;
  optionLabel: string;
  option: ComparisonSliderOption;
  resolvedPosterUrl: string | undefined;
  handlePosterError: () => void;
  currentShortDescLength: number;
  videoTitleId: string;
  videoDescId: string;
  onOpenVideoModal: (option: ComparisonSliderOptionKey) => void;
  onRemoveVideo: (option: ComparisonSliderOptionKey) => void;
  onVideoMetadataChange: (
    option: ComparisonSliderOptionKey,
    updates: { videoTitle?: string; videoShortDescription?: string }
  ) => void;
  styles: ComparisonSliderEditorStyles;
  translationMode: boolean;
}

function VideoFields({
  optionKey,
  optionLabel,
  option,
  resolvedPosterUrl,
  handlePosterError,
  currentShortDescLength,
  videoTitleId,
  videoDescId,
  onOpenVideoModal,
  onRemoveVideo,
  onVideoMetadataChange,
  styles,
  translationMode,
}: VideoFieldsProps) {
  const hasVideo = Boolean(option.videoUrl);

  return (
    <>
      <div>
        {translationMode ? (
          hasVideo && resolvedPosterUrl ? (
            <img
              src={resolvedPosterUrl}
              alt={option.videoTitle || 'Video thumbnail'}
              style={styles.videoPreview}
              crossOrigin="anonymous"
              onError={handlePosterError}
            />
          ) : null
        ) : <button
          type="button"
          style={styles.videoPickerButton}
          onClick={() => onOpenVideoModal(optionKey)}
          aria-label={
            hasVideo
              ? `Change ${optionLabel.toLowerCase()} video`
              : `Add ${optionLabel.toLowerCase()} video`
          }
        >
          {hasVideo ? (
            <>
              {resolvedPosterUrl ? (
                <img
                  src={resolvedPosterUrl}
                  alt={option.videoTitle || 'Video thumbnail'}
                  style={styles.videoPreview}
                  crossOrigin="anonymous"
                  onError={handlePosterError}
                />
              ) : (
                <div style={styles.videoPlaceholder}>
                  {VIDEO_ICON_SVG}
                </div>
              )}
              <div style={styles.videoOverlay}>Click to change video</div>
            </>
          ) : (
            <div style={styles.videoPlaceholder}>
              {VIDEO_ICON_SVG}
              <span>Click to select video</span>
            </div>
          )}
        </button>}
      </div>

      {!translationMode && hasVideo && (
        <button
          type="button"
          style={styles.removeImageButton}
          onClick={() => onRemoveVideo(optionKey)}
          aria-label={`Remove ${optionLabel.toLowerCase()} video`}
        >
          Remove video
        </button>
      )}

      <div>
        <div style={styles.panelFieldLabel}>
          Video title <span style={{ fontWeight: 400 }}>optional</span>
        </div>
        <input
          id={videoTitleId}
          type="text"
          value={option.videoTitle || ''}
          onChange={(e) =>
            onVideoMetadataChange(optionKey, { videoTitle: e.target.value })
          }
          placeholder="Enter video title..."
          style={styles.videoMetadataInput}
          aria-label={`${optionLabel} video title`}
        />
      </div>

      <div>
        <div style={styles.panelFieldLabel}>
          Video short description <span style={{ fontWeight: 400 }}>optional</span>
        </div>
        <textarea
          id={videoDescId}
          value={option.videoShortDescription || ''}
          onChange={(e) =>
            onVideoMetadataChange(optionKey, {
              videoShortDescription: e.target.value,
            })
          }
          placeholder="Enter short description..."
          style={styles.videoMetadataTextarea}
          rows={2}
          maxLength={VIDEO_SHORT_DESCRIPTION_MAX_LENGTH}
          aria-label={`${optionLabel} video short description`}
        />
        <div style={styles.videoCharacterCount}>
          {currentShortDescLength}/{VIDEO_SHORT_DESCRIPTION_MAX_LENGTH}
        </div>
      </div>
    </>
  );
}
